import { useState } from 'react'

export interface PaginationState {
  count: number
  offset: number
}

export interface SortingState {
  field: string
  direction: 'asc' | 'desc'
}

interface UsePaginationHook {
  pagination: PaginationState
  sorting: SortingState
  currentPage: number
  totalPages: number
  setPage: (page: number) => void
  setPageSize: (size: number) => void
  setSorting: (field: string, direction?: 'asc' | 'desc') => void
  setTotalCount: (count: number) => void
}

interface UsePaginationParams {
  initialPageSize?: number
  initialSortField?: string
  initialSortDirection?: 'asc' | 'desc'
}

export function usePagination({
  initialPageSize = 10,
  initialSortField = 'date',
  initialSortDirection = 'desc'
}: UsePaginationParams = {}): UsePaginationHook {
  const [pagination, setPagination] = useState<PaginationState>({
    count: initialPageSize,
    offset: 0
  })

  const [sorting, setSortingState] = useState<SortingState>({
    field: initialSortField,
    direction: initialSortDirection
  })

  const [totalCount, setTotalCount] = useState(0)

  const currentPage = Math.floor(pagination.offset / pagination.count) + 1
  const totalPages = Math.ceil(totalCount / pagination.count)

  const setPage = (page: number) => {
    setPagination(prev => ({
      ...prev,
      offset: (page - 1) * prev.count
    }))
  }

  const setPageSize = (size: number) => {
    setPagination(prev => ({
      count: size,
      offset: 0 // Reset to first page when changing page size
    }))
  }

  const setSorting = (field: string, direction?: 'asc' | 'desc') => {
    setSortingState(prev => ({
      field,
      direction: direction ?? (prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc')
    }))
  }

  return {
    pagination,
    sorting,
    currentPage,
    totalPages,
    setPage,
    setPageSize,
    setSorting,
    setTotalCount
  }
}
